import { ContractorLatestLogin } from './ContractorLatestLogin';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge, badgeVariants } from './ui/badge';
import React, { useState ,useEffect} from 'react';

import { Progress } from './ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  CreditCard, 
  Receipt, 
  FolderKanban, 
  Target, 
  Briefcase,
  Calendar,
  DollarSign,
  Users,
  MapPin,
  Star,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Building2,
  Sparkles,
  PauseCircle,
  RotateCcw,
  ShieldCheck
} from 'lucide-react';
import { CallAPIDeletePromise, CallAPIGetPromise, CallAPIPostPromise, grayColor } from 'Common/commaon';
import { getDecodeId } from 'slices/userProfile/thunk';
import { ContractorDetailsSkeleton } from './shimmer';
import { getInitials } from './ContractorList';
import moment from 'moment';
import { Button } from './ui/button';
import { TwoButtonAlert } from 'Common/twoButtonAlert';
import { IoTrashOutline } from 'react-icons/io5';
import { Toaster, toast } from 'sonner';
import { IoPauseOutline } from 'react-icons/io5';
import { IoPlayOutline } from 'react-icons/io5';
import RefundReasonModal from './refundModal';
import { useNavigate } from 'react-router-dom';

interface ContractorDetailsProps {
  contractorId: any;
  goToBack:any
}
export const getCardBrandColor = (brand: string) => {
  switch (String(brand || '').toLowerCase()) {
    case 'visa':
      return { 
        border: 'border-blue-200', 
        borderColor: '#BFDBFE',      // 💙 blue-200
        bg: 'bg-blue-50/50', 
        bgColor: '#EFF6FF',          // 💙 blue-50
        accent: 'bg-blue-600',
        accentColor: '#2563EB',      // 💙 blue-600
        text: 'text-blue-700',
        textColor: '#1D4ED8',        // 💙 blue-700
      };
    case 'mastercard':
      return { 
        border: 'border-orange-200', 
        borderColor: '#FED7AA',      // 🧡 orange-200
        bg: 'bg-orange-50/50', 
        bgColor: '#FFF7ED',          // 🧡 orange-50
        accent: 'bg-orange-600',
        accentColor: '#EA580C',      // 🧡 orange-600
        text: 'text-orange-700',
        textColor: '#C2410C',        // 🧡 orange-700
      };
    case 'american express':
      return { 
        border: 'border-blue-200', 
        borderColor: '#BFDBFE',      // 💙 blue-200
        bg: 'bg-blue-50/50', 
        bgColor: '#EFF6FF',          // 💙 blue-50
        accent: 'bg-blue-700',
        accentColor: '#1D4ED8',      // 💙 blue-700
        text: 'text-blue-800',
        textColor: '#1E40AF',        // 💙 blue-800
      };
    case 'discover':
      return { 
        border: 'border-orange-200', 
        borderColor: '#FED7AA',      // 🧡 orange-200
        bg: 'bg-orange-50/50', 
        bgColor: '#FFF7ED',          // 🧡 orange-50
        accent: 'bg-orange-500',
        accentColor: '#F97316',      // 🧡 orange-500
        text: 'text-orange-700',
        textColor: '#C2410C',        // 🧡 orange-700
      };
    default:
      return { 
        border: 'border-gray-200', 
        borderColor: '#E5E7EB',      // ⚪ gray-200
        bg: 'bg-gray-50', 
        bgColor: '#F9FAFB',          // ⚪ gray-50
        accent: 'bg-gray-600',
        accentColor: '#4B5563',      // ⚪ gray-600
        text: 'text-gray-700',
        textColor: '#374151',        // ⚪ gray-700
      };
  }
};

type PaymentProvider = 'stripe' | 'deluxe' | 'unknown';

const getContractorPaymentProvider = (
  contractor: any,
  subscriptions: any[]
): PaymentProvider => {
  const explicitProvider = String(
    contractor?.paymentProvider || contractor?.payment_provider || ''
  ).toLowerCase();

  if (explicitProvider === 'stripe' || explicitProvider === 'deluxe') {
    return explicitProvider;
  }

  const latestSubscription = [...(subscriptions || [])].sort((a, b) => {
    const aDate = new Date(a?.started_at || a?.created_at || 0).getTime();
    const bDate = new Date(b?.started_at || b?.created_at || 0).getTime();
    return bDate - aDate;
  })[0];

  if (latestSubscription?.deluxe_subscription_id) return 'deluxe';
  if (subscriptions?.some(subscription => subscription?.stripe_subscription_id)) return 'stripe';

  // The billing flow uses Deluxe for accounts without Stripe history.
  return 'deluxe';
};

const formatCardExpiry = (expiry: any, month?: any, year?: any) => {
  if (expiry) {
    const value = String(expiry).trim();
    const digits = value.replace(/\D/g, '');

    if (/^\d{4}$/.test(digits)) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    if (/^\d{6}$/.test(digits)) {
      return `${digits.slice(0, 2)}/${digits.slice(-2)}`;
    }

    return value;
  }

  if (!month && !year) return 'Not available';
  return `${String(month || '').padStart(2, '0')}/${String(year || '').slice(-2)}`;
};

const getTransactionProvider = (transaction: any): PaymentProvider => {
  const explicitProvider = String(
    transaction?.payment_provider || transaction?.paymentProvider ||
    transaction?.provider || transaction?.gateway || ''
  ).toLowerCase();

  if (explicitProvider === 'stripe' || explicitProvider === 'deluxe') {
    return explicitProvider;
  }

  if (transaction?.deluxe_transaction_id || transaction?.deluxe_customer_id) return 'deluxe';
  if (
    transaction?.stripe_transaction_id || transaction?.stripe_payment_intent_id ||
    transaction?.stripe_customer_id
  ) return 'stripe';

  return 'unknown';
};

const getReadableFailureReason = (reason: any) => {
  const rawReason = String(reason || '').trim();
  if (!rawReason) return { label: '—', rawReason: '' };

  const normalizedReason = rawReason.toLowerCase();
  if (normalizedReason.includes('trans denied') || normalizedReason.includes('declin')) {
    return { label: 'Transaction declined', rawReason };
  }
  if (normalizedReason.includes('insufficient')) {
    return { label: 'Insufficient funds', rawReason };
  }
  if (normalizedReason.includes('expired')) {
    return { label: 'Card expired', rawReason };
  }
  if (normalizedReason.includes('do_not_honor')) {
    return { label: 'Issuer declined the payment', rawReason };
  }

  return {
    label: rawReason.replace(/_/g, ' ').replace(/\s+/g, ' '),
    rawReason,
  };
};

const formatPhoneNumber = (value: any) => {
  const originalValue = String(value || '').trim();
  const digits = originalValue.replace(/\D/g, '');

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return originalValue || 'Not provided';
};




export function ContractorDetails({
  contractorId,
  goToBack
}: ContractorDetailsProps) {
    const [contractor,setContractor]=useState<any>('')
    const [subscriptions,setSubscriptions]=useState<any[]>([])
    const [transactions,setTransactions]=useState<any[]>([])
    const [projects,setProject]=useState<any[]>([])
    const [leads,setLeads]=useState<any[]>([])
    const [transactionDetails,setTransactionDetails]=useState<any>("")
    const [expanded, setExpanded] = useState(false);
  const [loading,setLoading]=useState(false)
    const [refundModalStatus,setRefundModalStatus]=useState(false)
    const [recentWork,setRecentWork]=useState<any[]>([])
    const activeSubcription = subscriptions.filter(s=>s.status==="active") as any
  const paymentProvider = getContractorPaymentProvider(contractor, subscriptions)
  const providerLabel = paymentProvider === 'deluxe'
    ? 'Deluxe'
    : paymentProvider === 'stripe'
      ? 'Stripe'
      : 'payment provider'
  const savedCards = (Array.isArray(contractor?.cardResult) ? contractor.cardResult : []).filter((card: any) => {
    const cardProvider = String(
      card?.provider || card?.paymentProvider || card?.payment_provider || card?.gateway ||
      (card?.card ? 'stripe' : (card?.maskedPan || card?.cardType) ? 'deluxe' : paymentProvider)
    ).toLowerCase()

    return paymentProvider === 'unknown' || cardProvider === paymentProvider
  })
  const sortedTransactions = [...transactions].sort((a: any, b: any) => {
    return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime()
  })
  const currentSubscription = activeSubcription[0]
  const providerSubscriptionId = paymentProvider === 'deluxe'
    ? currentSubscription?.deluxe_subscription_id
    : currentSubscription?.stripe_subscription_id
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
const navigate =useNavigate()

  const [alertDetails,setAlertDetails]= useState({
    status: false,
    title: "Warning!",
    message: "You’re about to delete your account forever,",
    icon: IoTrashOutline,
    button1Text: "Keep it",
    button2Text: "Delete",
    button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
    button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
    onButton1Click: () => console.log("Saved "),
    onButton2Click: () => console.log("Deleted "),
  })

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'monthly':
        return 'bg-green-500/10 text-green-700 border-green-200';
      case 'yearly':
        return 'bg-gray-500/10 text-gray-700 border-gray-200';
      case null:
      case undefined:
      case '':
        return 'bg-red-500/10 text-red-700 border-red-200';
      default:
        return '';
    }
  };
  const getStatusBadge = (status: string, type: 'subscription' | 'transaction' | 'project' | 'lead') => {
    const configs = {
      subscription: {
        active: { color: 'bg-green-500/10 text-green-700 border-green-200', icon: CheckCircle2 },
        cancelled: { color: 'bg-gray-500/10 text-gray-700 border-gray-200', icon: XCircle },
        paused: { color: 'bg-red-500/10 text-red-700 border-red-200', icon: AlertCircle }
      },
      transaction: {
        succeeded: { 
          color: "bg-green-500/10 text-green-700 border-green-200", 
          icon: CheckCircle2 
        },
        pending: { 
          color: "bg-yellow-500/10 text-yellow-700 border-yellow-200", 
          icon: Clock 
        },
        failed: { 
          color: "bg-red-500/10 text-red-700 border-red-200", 
          icon: XCircle 
        },
        refunded: { 
          color: "bg-blue-500/10 text-blue-700 border-blue-200", 
          icon: RotateCcw
        },
      },
    
      project: {
        DRAFT: { 
          color: "bg-gray-100 text-gray-700 border-gray-300", 
          icon: Target 
        },
        LIVE: { 
          color: "bg-blue-100 text-blue-700 border-blue-300", 
          icon: Clock 
        },
        COMPLETED: { 
          color: "bg-green-100 text-green-700 border-green-300", 
          icon: CheckCircle2 
        },
        "ON-HOLD": { 
          color: "bg-yellow-100 text-yellow-700 border-yellow-300", 
          icon: PauseCircle
        },
        CANCELLED: { 
          color: "bg-red-100 text-red-700 border-red-300", 
          icon: AlertCircle 
        },
      },
      lead: {
        OPEN: { color: 'bg-blue-500/10 text-blue-700 border-blue-200', icon: Target },
        requested: { color: 'bg-purple-500/10 text-purple-700 border-purple-200', icon: Clock },
        accepted: { color: 'bg-green-500/10 text-green-700 border-green-200', icon: CheckCircle2 },
        declined: { color: 'bg-gray-500/10 text-gray-700 border-gray-200', icon: XCircle }
      },
      plan_type: {
        active: { color: 'bg-green-500/10 text-green-700 border-green-200', icon: CheckCircle2 },
        cancelled: { color: 'bg-gray-500/10 text-gray-700 border-gray-200', icon: XCircle },
        paused: { color: 'bg-red-500/10 text-red-700 border-red-200', icon: AlertCircle }
      },
    } as any

    const config = configs[type][status as keyof typeof configs[typeof type]];
    const Icon = config?.icon;

    return (
      <Badge variant="outline" className={config?.color}>
        {Icon && <Icon className="h-3 w-3 mr-1" />}
        {status}
      </Badge>
    );
  };

  const openLeads = contractor?.accepted_leads
  const requestedLeads = contractor?.req_leads
useEffect(()=>{
if(contractorId){
  setLoading(true)
    CallAPIGetPromise(`/api/crm/contractorDetails/${contractorId}`).then((res:any)=>{
        setContractor(res.data)
        setSubscriptions(res.data.subscriptionDetails)
        setRecentWork(res.data.recent_works ||[])
        setTransactions(res.data.transactionDetails ||[])
        setProject(res.data.all_projects ||[])
        setLeads(res.data.req_leads ||[])
    }).finally(()=>{
      setLoading(false)
    })
}

},[contractorId])
if(loading){
  return <ContractorDetailsSkeleton />
}
const ContractrType = contractor?.ua_profile ?JSON.parse(contractor?.ua_profile) :{label:"Contractor"}
  return (

    <div className="space-y-4 tailwind-prefix">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
        {contractor?.ua_profile_pic ? <img alt="" className="h-12 w-12 rounded-full object-cover" src={contractor.ua_profile_pic}/> :            <Avatar className="h-12 w-12 ">
              <AvatarFallback className="bg-blue-600 text-white">
                {contractor.user_first_name&& getInitials(contractor.user_first_name)}
              </AvatarFallback>
            </Avatar>}
          <div>
            <div className="text-gray-900 font-semibold mb-1">{contractor.user_first_name + " " +contractor.user_last_name}</div>
            {contractor.company_name && <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200 w-fit mb-1.5">
              <Building2 className="h-4 w-4 text-blue-600" />
              <div className="text-blue-700">{contractor.company_name}</div>
            </div>}
            <div className="text-gray-600">{ContractrType.label}</div>
          </div>
        </div>
  
       
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-50 p-2 text-blue-600"><FolderKanban className="h-4 w-4" /></div>
            <div><div className="text-gray-600 text-xs">Total Projects</div><div className="text-gray-900 font-semibold mt-1">{projects.length}</div></div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-50 p-2 text-purple-600"><Target className="h-4 w-4" /></div>
            <div><div className="text-gray-600 text-xs">Active Leads</div><div className="text-gray-900 font-semibold mt-1">{leads?.length}</div></div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-50 p-2 text-green-600"><CreditCard className="h-4 w-4" /></div>
            <div><div className="text-gray-600 text-xs">Subscription</div><div className="text-gray-900 font-semibold mt-1 capitalize">{subscriptions.find(s=>s.status==="active")?.plan_type || 'None'}</div></div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-orange-50 p-2 text-orange-600"><Briefcase className="h-4 w-4" /></div>
            <div><div className="text-gray-600 text-xs">Recent Work</div><div className="text-gray-900 font-semibold mt-1">{recentWork?.length} completed</div></div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="contractor-details-tabs w-full border-0">
        <TabsList className="grid h-auto w-full grid-cols-2 sm:grid-cols-4 xl:grid-cols-7" >
          <TabsTrigger className='' value="overview">Overview</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="cards">Payment Methods</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="work">Recent Work</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 ">
          <ContractorLatestLogin contractorId={contractorId} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-gray-600 mb-1">Email</div>
                  <div className="text-gray-900">{contractor.user_email}</div>
                </div>
                <div>
                  <div className="text-gray-600 mb-1">Phone</div>
                  <div className="text-gray-900">{formatPhoneNumber(contractor.user_phone)}</div>
                </div>
                <div>
                  <div className="text-gray-600 mb-1">Company</div>
                  <div className="text-gray-900">{contractor.company_name}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Current Subscription</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {activeSubcription.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Plan</span>
                      <Badge variant="outline" className={getStatusColor(contractor.active_plan)}>
                    {contractor.active_plan ? contractor.active_plan : 'Not Subscribed'}
                  </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Status</span>
                      {getStatusBadge(activeSubcription[0].status, 'subscription')}
                    </div>
                    {providerSubscriptionId && <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-600">{providerLabel} subscription ID</span>
                      <span className="text-gray-900 text-right break-all">{providerSubscriptionId}</span>
                    </div>}
                    <div className='flex justify-end'>
                    {/* <Button  onClick={() =>{
                        CallAPIPostPromise(`/v2/api/subscription/extend-trail-days`,{
                          userId:1258,
                          extendDays:50
                        }).then((res:any)=>{
                    console.log(res)
                       }).catch((e)=>{
                        console.log(e)
                       }).finally(()=>{
                    setLoading(false)
                  toast.success("Subscription cancelled")

                  })
                     
                      
                    }}  variant={'ghost'} className='border-0'>
                      Extend 30 days trail
                      </Button> */}
                    <button type="button" onClick={() =>{
                      setAlertDetails({
                        status: true,
                        title: "Cancel subscription?",
                        message: `You’re about to cancel the subscription for ${contractor.user_email}.`,
                        icon: XCircle,
                        button1Text: "Close",
                        button2Text: "Cancel subscription",
                        button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
                        button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
                        onButton1Click: () => console.log("Saved "),
                        onButton2Click: () => {
                          // setLoading(true)
                          CallAPIPostPromise(`/stripe-sub/cancel-subscription`,{
                            // The admin cancellation route uses the database record to
                            // cancel the correct Stripe or Deluxe subscription.
                            subscription_id: activeSubcription[0].id
                          }).then(()=>{
                             CallAPIGetPromise(`/api/crm/contractorDetails/${contractorId}`).then((res:any)=>{
                                 setContractor(res.data)
                                 setSubscriptions(res.data.subscriptionDetails)
                                 setRecentWork(res.data.recent_works ||[])
                                 setTransactions(res.data.transactionDetails ||[])
                                 setProject(res.data.all_projects ||[])
                                 setLeads(res.data.req_leads ||[])
                             }).finally(()=>{
                               setLoading(false)
                             toast.success("Subscription cancelled")

                             })
                          
                            
                          }).catch((e)=>{
                            console.log(e)
                          }).finally(()=>{
                          })
                        },
                      })
                     
                      
                    }} className='contractor-cancel-subscription-button'>
                        <XCircle className="h-4 w-4" />
                        Cancel Subscription
                      </button>
                    </div>
                  
                    {/* <div className="flex items-center justify-between">
                      <span className="text-gray-600">Renewal</span>
                      <span className="text-gray-900">{formatDate(subscriptions[0].endDate)}</span>
                    </div> */}
                  </>
                ) : (
                  <div className="text-gray-500">No active subscription</div>
                )}
              </CardContent>
            </Card>
          </div>
          {contractor.ua_skills &&  <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Skills & Expertise
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {JSON.parse(contractor.ua_skills) .map((skill:any, index:any) => (
                  <Badge 
                    key={index} 
                    variant="outline" 
                    className="bg-purple-50 text-purple-700 border-purple-200 px-3 py-1"
                  >
                    {skill.label}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>}
          <div className="mt-8 border border-red-200 rounded-xl p-6 bg-red-50">
  <h3 className="text-lg font-semibold text-red-600 mb-4">
    Danger Zone
  </h3>

  <div className="flex gap-4">
  <Button
  onClick={() => {
    const isActive = contractor?.is_active === 1;
    const actionType = isActive ? "pause" : "active";

    setAlertDetails({
      status: true,
      title: "Warning!",
      message: isActive
        ? `You’re about to pause the account associated with ${contractor.user_email}. This action will prevent the contractor from signing in and remove their visibility from the platform.`
        : `You’re about to reactivate the account associated with ${contractor.user_email}. The contractor will regain access to the platform and become visible again.`,
      icon: isActive ? IoPauseOutline : IoPlayOutline,
      button1Text: "Close",
      button2Text: isActive ? "Pause Account" : "Activate Account",
      button1Style:
        "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
      button2Style: isActive
        ? "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition"
        : "px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition",

      onButton1Click: () => {},

      onButton2Click: async () => {
        try {
          setLoading(true);

          await CallAPIPostPromise(`/admin/accountAction`, {
            id: contractor.id,
            action: actionType,
            byUserId:getDecodeId()
          });

          const res: any = await CallAPIGetPromise(
            `/api/crm/contractorDetails/${contractorId}`
          );

          setContractor(res.data);
          setSubscriptions(res.data.subscriptionDetails);
          setRecentWork(res.data.recent_works || []);
          setTransactions(res.data.transactionDetails || []);
          setProject(res.data.all_projects || []);
          setLeads(res.data.req_leads || []);

          toast.success(
            actionType === "pause"
              ? "Account paused successfully"
              : "Account activated successfully"
          );
        } catch (e) {
          console.log(e);
          toast.error("Something went wrong");
        } finally {
          setLoading(false);
        }
      },
    });
  }}
  variant="ghost"
  className={`${
    contractor?.is_active === 1
      ? "bg-white border border-red-500 text-red-600"
      : "bg-green-600 text-white hover:bg-green-700"
  }`}
>
  {contractor?.is_active === 1
    ? "Pause Account"
    : "Activate Account"}
</Button>

    <Button onClick={()=>{
         setAlertDetails({
          status: true,
          title: "Warning!",
          message: `You’re about to permanently delete the account for ${contractor.user_email}. This action is irreversible and will remove all associated data from the platform.`,
          icon: IoTrashOutline,
          button1Text: "Close",
          button2Text: "Delete Account",
          button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
          button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
          onButton1Click: () => console.log("Saved "),
          onButton2Click: () => {
            setLoading(true)
            CallAPIPostPromise(`/admin/deleteAccount`,{
              id: contractor.id,
            }).then(()=>{
              goToBack()
              
            }).catch((e)=>{
              console.log(e)
            }).finally(()=>{
            })
          },
        })
    }} variant={"destructive"} >
      Delete Account
    </Button>
  </div>
</div>
         
          <Card>
            <div className='px-4 py-3'>
              <CardTitle>Active Projects Summary</CardTitle>
            </div>
            <CardContent>
              <div className="space-y-4">
                {contractor.live_projects?.sort((a: { created_date: string }, b: { created_date: string }) => {
    const dateA = new Date(a.created_date).getTime();
    const dateB = new Date(b.created_date).getTime();
    return dateB - dateA; // ✅ both are clearly numbers
  }).map((project:any) => (
                  <div key={project.id} className="p-3 border rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-gray-900 mb-1 font-semibold">{project.name}</div>
                        <div className="text-gray-600">{project.homeowner.name}</div>
                      </div>
                      {getStatusBadge(project.status, 'project')}
                    </div>
                    {/* <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Progress</span>
                        <span className="text-gray-900">{project.progress}%</span>
                      </div>
                      <Progress value={project.progress} />
                    </div> */}
                  </div>
                ))}
                {contractor.live_projects?.length === 0 && (
                  <div className="text-gray-500 text-center py-8">No active projects</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <CreditCard className="h-5 w-5" />
        Subscription History
      </CardTitle>
      <CardDescription>All subscription plans and their status</CardDescription>
    </CardHeader>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Plan</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Provider / ID</TableHead>
            <TableHead>Start Date</TableHead>
            <TableHead>End Date</TableHead>
            <TableHead>Logs</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {subscriptions.map((subscription) => {
            const subscriptionProvider = subscription.deluxe_subscription_id ? 'Deluxe' : 'Stripe'
            const subscriptionId = subscription.deluxe_subscription_id || subscription.stripe_subscription_id
            const relatedLogs = (contractor.subcriptionLogResult || []).filter(
              (log:any) => log.subscription_id === subscriptionId
            );

            return (
              <TableRow key={subscription.id}>
                {/* Plan Type */}
                <TableCell className="capitalize">{subscription.plan_type}</TableCell>

                {/* Status */}
                <TableCell>
                  {getStatusBadge(subscription.status, 'subscription')}
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">{subscriptionProvider}</Badge>
                    <span className="font-mono text-xs">{subscriptionId || '—'}</span>
                  </div>
                </TableCell>

                {/* Dates */}
                <TableCell>{formatDate(subscription.started_at)}</TableCell>
                <TableCell>
                  {subscription.ended_at ? formatDate(subscription.ended_at) : '-'}
                </TableCell>

                {/* Logs */}
                <TableCell>
                  {relatedLogs?.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {relatedLogs.map((log:any, index:any) => (
                        <div
                          key={index}
                          className="text-xs bg-muted/50 rounded-md p-2 border border-muted"
                        >
                          <div>
                            <strong>{log.status}</strong> — {formatDate(log.created_at)}
                          </div>
                          {log.reason_for_cancellation && (
                            <div className="text-muted-foreground">
                              {log.reason_for_cancellation}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">No logs</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
  </Card>
</TabsContent>


        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader className="px-4 pt-4 pb-3 border-b">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Transactions
              </CardTitle>
              <CardDescription>View payment history and logs</CardDescription>
            </CardHeader>

            <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[150px] px-4">Created</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="min-w-[220px]">Details</TableHead>
              <TableHead className="text-right px-4">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className='px-3'>
  {sortedTransactions.length > 0 ? (
    sortedTransactions.map((transaction) => {
      const logs = (contractor?.transactionLogResult || contractor?.subcriptionLogResult || []).filter(
        (log: any) => log.transaction_id === transaction.id
      );
      const transactionProvider = getTransactionProvider(transaction)
      const failureReason = getReadableFailureReason(transaction.failure_reason)


      return (
        <React.Fragment key={transaction.id}>
          <TableRow className={logs.length > 0 ? "cursor-pointer hover:bg-gray-50" : "hover:bg-gray-50"} onClick={() => {
            if (logs.length === 0) return
            setExpanded( e=>e===transaction.id?false:transaction.id)
          }}>
            <TableCell className="px-4">
              <div className="font-medium text-gray-900">{moment(transaction.created_at).format("MMM D, YYYY")}</div>
              <div className="text-xs text-gray-500 mt-0.5">{moment(transaction.created_at).format("h:mm A")}</div>
            </TableCell>
            <TableCell className="capitalize">{transaction.type}</TableCell>
            <TableCell>{getStatusBadge(transaction.status, 'transaction')}</TableCell>
            <TableCell>
              <Badge variant="outline" className={transactionProvider === 'deluxe'
                ? 'bg-purple-500/10 text-purple-700 border-purple-200 capitalize'
                : transactionProvider === 'stripe'
                  ? 'bg-blue-500/10 text-blue-700 border-blue-200 capitalize'
                  : 'bg-gray-500/10 text-gray-700 border-gray-200'}>
                {transactionProvider === 'unknown' ? 'Other' : transactionProvider}
              </Badge>
            </TableCell>
            <TableCell className="text-right font-medium">{formatCurrency(transaction.amount)}</TableCell>
            <TableCell className="whitespace-normal max-w-[280px]" title={failureReason.rawReason}>
              <div className="text-gray-700">{failureReason.label}</div>
              {failureReason.rawReason && failureReason.label !== failureReason.rawReason && (
                <div className="text-xs text-gray-500 mt-0.5">{failureReason.rawReason}</div>
              )}
            </TableCell>
            <TableCell className="text-right px-4">
            {transaction.status === "succeeded" && transaction.deluxe_transaction_id && transaction.amount && (
  <button
  type="button"
  onClick={(event)=>{
    event.stopPropagation()
    setRefundModalStatus(true)
    setTransactionDetails(transaction)
  
  }}
    className="contractor-refund-button"
  >
    <RotateCcw className="h-3.5 w-3.5" />
    Refund
  </button>
)}
            </TableCell>
          </TableRow>

          {(expanded === transaction.id||(logs[0]?.old_status &&logs[0]?.old_status !==logs[0]?.new_status))&& logs?.length > 0 && (
            <TableRow>
              <TableCell colSpan={7} className="bg-gray-50 p-2">
                <Table >
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Old Status → New Status</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log: any) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.event_type}</TableCell>
                        <TableCell>{(log.old_status || 'null') + ' → ' } {getStatusBadge(log.new_status, 'transaction')}</TableCell>
                        <TableCell>{formatDate(log.created_at)}</TableCell>
                        <TableCell>
                          <details className="cursor-pointer">
                            <summary className="text-blue-600 hover:underline">View Metadata</summary>
                            <pre className="bg-gray-100 dark:bg-[#1F2937] p-2 mt-2 rounded text-xs overflow-x-auto whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                              {JSON.stringify(
                                (() => {
                                  const meta = log.metadata || {};
                                  if (meta.phases || meta.metadata) {
                                    return {
                                      subscription: meta.subscription,
                                      status: meta.status,
                                      customer: meta.customer,
                                      planType: meta.metadata?.planType,
                                      videoPackage: meta.metadata?.videoPackage,
                                      phases: meta.phases?.map((p:any) => ({
                                        start_date: p.start_date,
                                        end_date: p.end_date,
                                        plan: p.items?.[0]?.plan,
                                      })) || [],
                                    };
                                  }
                                  return meta; // flat metadata
                                })(),
                                null,
                                2
                              )}
                            </pre>
                          </details>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableCell>
            </TableRow>
          )}
        </React.Fragment>
      );
    })
  ) : (
    <TableRow>
      <TableCell colSpan={7} className="text-center text-gray-500 py-8">
        No transactions found
      </TableCell>
    </TableRow>
  )}
</TableBody>

        </Table>
          </Card>
        </TabsContent>
        <TabsContent value="cards" className="space-y-4">
          <Card>
            <CardHeader className="px-4 pt-4 pb-3 border-b">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payment Methods
              </CardTitle>
              <CardDescription className="flex items-center gap-2">
                Cards stored with
                <Badge variant="outline" className="capitalize">{providerLabel}</Badge>
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {savedCards.map((d:any) =>{
                  const isStripeCard = paymentProvider === 'stripe'
                  const card = isStripeCard ? d?.card || {} : d
                  const brand = isStripeCard ? card?.brand : card?.cardType
                  const last4 = String(
                    card?.last4 || card?.maskedPan || ''
                  ).replace(/\D/g, '').slice(-4)
                  const maskedNumber = last4 ? `•••• ${last4}` : 'Saved payment method'
                  const expiry = isStripeCard
                    ? formatCardExpiry(null, card?.exp_month, card?.exp_year)
                    : formatCardExpiry(card?.expiry)
                  const billingDetails = d?.billing_details || card?.billing_details
    return (
      <div
        key={d?.paymentMethodId || d?.id || card?.id}
        className="contractor-payment-method-card"
      >
        {/* Card header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="contractor-payment-method-card__icon">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <div className="text-gray-900 font-semibold capitalize">{brand || 'Card'}</div>
              <div className="text-gray-500 mt-0.5">{maskedNumber}</div>
            </div>
          </div>
          {d.isPrimary && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Default
            </Badge>
          )}
        </div>

        {/* Card details */}
        <div className="contractor-payment-method-card__details">
          <div>
            <div className="text-gray-500 mb-1">Expires</div>
            <div className="text-gray-900 font-medium">{expiry}</div>
          </div>
          {billingDetails?.name && (
            <div>
              <div className="text-gray-500 mb-1">Card Holder</div>
              <div className="text-gray-900 font-medium">{billingDetails.name}</div>
            </div>
          )}
          {billingDetails?.address?.postal_code && (
            <div>
              <div className="text-gray-500 mb-1">ZIP code</div>
              <div className="text-gray-900 font-medium">{billingDetails.address.postal_code}</div>
            </div>
          )}
        </div>

          <div className="contractor-payment-method-card__footer">
            <ShieldCheck className="h-4 w-4" />
            <span>Secured by {providerLabel}</span>
          </div>
      </div>
    );
  })
  }
              </div>
              {savedCards.length === 0 && (
                <div className="py-12 text-center">
                  <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No {providerLabel} payment methods on file</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="space-y-4">
          {projects.sort((a: { created_date: string }, b: { created_date: string }) => {
    const dateA = new Date(a.created_date).getTime();
    const dateB = new Date(b.created_date).getTime();
    return dateB - dateA; // ✅ both are clearly numbers
  }).map((project) => (
            <Card key={project.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{project.project_name}</CardTitle>
                    <CardDescription>{project.description}</CardDescription>
                  </div>
                  {getStatusBadge(project.status, 'project')}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Budget:</span>
                      <span className="text-gray-900">{formatCurrency(project.cost)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Start:</span>
                      <span className="text-gray-900">{formatDate(project.created_date)}</span>
                    </div>
                    {/* {project.endDate && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">End:</span>
                        <span className="text-gray-900">{formatDate(project.endDate)}</span>
                      </div>
                    )} */}
                  </div>
                  {/* <div>
                    <div className="text-gray-600 mb-2">Progress</div>
                    <div className="space-y-2">
                      <Progress value={project.progress} />
                      <div className="text-gray-900">{project.progress}% complete</div>
                    </div>
                  </div> */}
                </div>

                <div style={{
                    borderTop:`1px solid ${grayColor}`
                  }} className="pt-4 border-t">
                  <div className="text-gray-900 mb-3">Homeowner</div>
                  <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                    <div className="text-gray-900">{project.homeowner.name}</div>
                    <div className="flex items-center gap-4 text-gray-600">
                      <span>{project.homeowner.email}</span>
                      <span>{project.homeowner.phone}</span>
                    </div>
                    {/* <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>{project.homeowner.address}</span>
                    </div> */}
                  </div>
                </div>

                {project?.subcontractors?.length > 0 && (
                  <div style={{
                    borderTop:`1px solid ${grayColor}`
                  }} className="pt-4 ">
                    <div className="text-gray-900 mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Subcontractors ({project.subcontractors?.length})
                    </div>
                    <div className="space-y-2">
                      {project.subcontractors.map((sub:any) => (
                        <div key={sub.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-gray-900">{sub.name}</div>
                            {/* <Badge variant="outline">{sub.role}</Badge> */}
                          </div>
                          <div className="flex items-center gap-4 text-gray-600">
                            <span>{sub.email}</span>
                            <span>{sub.phone}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {projects?.length === 0 && (
            <Card>
              <CardContent className="py-12">
                <div className="text-gray-500 text-center">No projects found</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Leads Tab */}
        <TabsContent value="leads" className="space-y-4">
          <div>
            <div className="text-gray-900 mb-3">Requested Leads ({requestedLeads?.length})</div>
            <div className="space-y-3">
              {requestedLeads?.map((lead:any) => (
                <Card key={lead.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-gray-900 mb-1">{lead.lead_sub_cat}</div>
                      <div className="text-gray-600">{lead.lead_description}</div>
                    </div>
                    {getStatusBadge(lead.lead_job_status, 'lead')}
                  </div>
                  <div className="flex items-center gap-6 text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{lead.lead_city}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      <span>{formatCurrency(lead.lead_budget)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(lead.lead_created_at)}</span>
                    </div>
                    <Badge variant="outline">{lead.lead_cat_id}</Badge>
                  </div>
                </CardContent>
              </Card>
              ))}
              {requestedLeads?.length === 0 && (
                <Card>
                  <CardContent className="py-8">
                    <div className="text-gray-500 text-center">No requested leads</div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          <div>
            <div className="text-gray-900 mb-3">Open Leads ({openLeads?.length})</div>
            <div className="space-y-3">
              {openLeads?.map((lead:any) => (
                <Card key={lead.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-gray-900 mb-1">{lead.lead_sub_cat}</div>
                        <div className="text-gray-600">{lead.lead_description}</div>
                      </div>
                      {getStatusBadge(lead.lead_job_status, 'lead')}
                    </div>
                    <div className="flex items-center gap-6 text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>{lead.lead_city}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        <span>{formatCurrency(lead.lead_budget)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(lead.lead_created_at)}</span>
                      </div>
                      <Badge variant="outline">{lead.lead_cat_id}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {openLeads?.length === 0 && (
                <Card>
                  <CardContent className="py-8">
                    <div className="text-gray-500 text-center">No open leads</div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Recent Work Tab */}
        <TabsContent value="work" className="space-y-4">
          {recentWork.map((work) => (
            <Card key={work.rw_id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{work.rw_name}</CardTitle>
                  </div>
                
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-gray-600 mb-3">{work.rw_description}</div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>Completed on {formatDate(work.rw_created_at)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
          {recentWork?.length === 0 && (
            <Card>
              <CardContent className="py-12">
                <div className="text-gray-500 text-center">No recent work to display</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      <RefundReasonModal open={refundModalStatus}
  onClose={()=>{
    setRefundModalStatus(false)
  }}
  onSubmit={(reason)=>{
    setRefundModalStatus(false)
      CallAPIPostPromise("/v2/api/deluxe/refund",{
      paymentId:transactionDetails?.deluxe_transaction_id,
      amount:transactionDetails?.amount,
      deluxe_customer_id:transactionDetails?.deluxe_customer_id,
      reason:reason,
      transactionRowId:transactionDetails?.id
    })

  }}/>
      <TwoButtonAlert setDetails={setAlertDetails}  details={alertDetails}/>
<Toaster  position={'top-right'}/>
    </div>
  );
}
